import { render } from 'preact';
import { Widget } from './components/Widget';
import { widgetStyles } from './styles';
import { initOnlineListener, resetState } from './store/state';
import type { WidgetConfig } from './types';

// Version injected by Vite at build time
declare const __VERSION__: string;
const VERSION = typeof __VERSION__ !== 'undefined' ? __VERSION__ : 'dev';

/**
 * EasyHelp Widget Entry Point
 *
 * Usage:
 * <script src="https://cdn.easyhelp.cloud/widget.js"></script>
 * <script>
 *   EasyHelpWidget.init({
 *     apiUrl: 'https://your-project.supabase.co/functions/v1',
 *     tenantId: 'your-tenant-id',
 *     customerId: 'user-123',
 *     customerName: 'John Doe',
 *     theme: { primaryColor: '#2563eb' }
 *   });
 * </script>
 */

// Global widget instance
let widgetInstance: WidgetInstance | null = null;

interface WidgetInstance {
  config: WidgetConfig;
  container: HTMLDivElement;
  shadowRoot: ShadowRoot;
  cleanupOnline: () => void;
  destroy: () => void;
}

/**
 * Initialize the widget
 */
function init(config: WidgetConfig): WidgetInstance {
  // Validate required config
  if (!config.apiUrl) {
    throw new Error('EasyHelpWidget: apiUrl is required');
  }
  if (!config.tenantId) {
    throw new Error('EasyHelpWidget: tenantId is required');
  }
  if (!config.customerId) {
    throw new Error('EasyHelpWidget: customerId is required');
  }

  // Destroy existing instance if any
  if (widgetInstance) {
    widgetInstance.destroy();
  }

  // Create container element
  const container = document.createElement('div');
  container.id = 'easyhelp-widget-container';

  // Create Shadow DOM for CSS isolation
  const shadowRoot = container.attachShadow({ mode: 'closed' });

  // Inject styles into shadow DOM
  const styleEl = document.createElement('style');
  styleEl.textContent = widgetStyles;
  shadowRoot.appendChild(styleEl);

  // Apply CSS custom properties for theming
  const theme = config.theme || {};
  if (theme.primaryColor) {
    styleEl.textContent += `:host { --easyhelp-primary: ${theme.primaryColor}; }`;
  }
  if (theme.primaryTextColor) {
    styleEl.textContent += `:host { --easyhelp-primary-text: ${theme.primaryTextColor}; }`;
  }
  if (config.zIndex) {
    styleEl.textContent += `:host { --easyhelp-z-index: ${config.zIndex}; }`;
  }

  // Create mount point for Preact
  const mountPoint = document.createElement('div');
  mountPoint.className = 'easyhelp-widget-mount';
  shadowRoot.appendChild(mountPoint);

  // Add container to document
  document.body.appendChild(container);

  // Initialize online/offline listener
  const cleanupOnline = initOnlineListener();

  // Render the widget
  render(<Widget config={config} />, mountPoint);

  // Store instance
  widgetInstance = {
    config,
    container,
    shadowRoot,
    cleanupOnline,
    destroy: () => {
      cleanupOnline();
      resetState();
      render(null, mountPoint);
      container.remove();
      widgetInstance = null;
    },
  };

  return widgetInstance;
}

/**
 * Open the chat panel
 */
function open(): void {
  if (!widgetInstance) {
    console.warn('EasyHelpWidget: Widget not initialized');
    return;
  }
  // Dispatch custom event that Widget component listens for
  const event = new CustomEvent('easyhelp:open');
  widgetInstance.container.dispatchEvent(event);
}

/**
 * Close the chat panel
 */
function close(): void {
  if (!widgetInstance) {
    console.warn('EasyHelpWidget: Widget not initialized');
    return;
  }
  const event = new CustomEvent('easyhelp:close');
  widgetInstance.container.dispatchEvent(event);
}

/**
 * Toggle the chat panel
 */
function toggle(): void {
  if (!widgetInstance) {
    console.warn('EasyHelpWidget: Widget not initialized');
    return;
  }
  const event = new CustomEvent('easyhelp:toggle');
  widgetInstance.container.dispatchEvent(event);
}

/**
 * Destroy the widget
 */
function destroy(): void {
  if (widgetInstance) {
    widgetInstance.destroy();
  }
}

/**
 * Update customer info (e.g., after login)
 */
function updateCustomer(params: {
  customerId: string;
  customerName?: string;
  customerEmail?: string;
}): void {
  if (!widgetInstance) {
    console.warn('EasyHelpWidget: Widget not initialized');
    return;
  }

  // If customer changed, need to re-init with new token
  if (params.customerId !== widgetInstance.config.customerId) {
    const newConfig: WidgetConfig = {
      ...widgetInstance.config,
      customerId: params.customerId,
    };
    // Only set optional properties if they are defined
    if (params.customerName !== undefined) {
      newConfig.customerName = params.customerName;
    }
    if (params.customerEmail !== undefined) {
      newConfig.customerEmail = params.customerEmail;
    }
    init(newConfig);
  }
}

// Export public API
const EasyHelpWidget = {
  init,
  open,
  close,
  toggle,
  destroy,
  updateCustomer,
  version: VERSION,
};

// Expose to window for script tag usage
declare global {
  interface Window {
    EasyHelpWidget: typeof EasyHelpWidget;
  }
}

window.EasyHelpWidget = EasyHelpWidget;

export default EasyHelpWidget;
